import React, { useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link2, Image as ImageIcon, Code, Code2, Minus, Undo, Redo,
} from 'lucide-react'
import styles from './RichTextEditor.module.scss'

const lowlight = createLowlight(common)

const LANGUAGES = [
  { value: '', label: 'Auto-detect' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'python', label: 'Python' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'json', label: 'JSON' },
  { value: 'bash', label: 'Bash' },
  { value: 'sql', label: 'SQL' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'yaml', label: 'YAML' },
  { value: 'xml', label: 'XML' },
  { value: 'markdown', label: 'Markdown' },
] as const

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  className?: never
}

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  title: string
  children: React.ReactNode
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={[styles.toolbarBtn, active ? styles.toolbarBtnActive : ''].filter(Boolean).join(' ')}
  >
    {children}
  </button>
)

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, disabled }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Link.configure({ openOnClick: false }),
      Image,
      CodeBlockLowlight.configure({ lowlight, defaultLanguage: null }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => { onChange(ed.getHTML()) },
  })

  const insertCodeBlock = useCallback(() => {
    if (!editor) return
    const language = window.prompt(
      `Enter language (${LANGUAGES.filter((l) => l.value).map((l) => l.value).join(', ')}):\n\nLeave blank for auto-detect.`
    )
    if (language === null) return
    editor.chain().focus().toggleCodeBlock().run()
    if (language) {
      editor.chain().focus().updateAttributes('codeBlock', { language }).run()
    }
  }, [editor])

  if (!editor) return null

  const setLink = () => {
    const url = window.prompt('Enter URL:')
    if (url) { editor.chain().focus().setLink({ href: url }).run() }
  }

  const addImage = () => {
    const url = window.prompt('Enter image URL:')
    if (url) { editor.chain().focus().setImage({ src: url }).run() }
  }

  const iconStyle = { width: 14, height: 14 }

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <Bold style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <Italic style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
          <List style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List">
          <ListOrdered style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline Code">
          <Code style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={insertCodeBlock} active={editor.isActive('codeBlock')} title="Code Block">
          <Code2 style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Add Link">
          <Link2 style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={addImage} title="Add Image">
          <ImageIcon style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
          <Minus style={iconStyle} />
        </ToolbarButton>
        <div className={styles.toolbarSpacer} />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo style={iconStyle} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo style={iconStyle} />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className={styles.content} />
    </div>
  )
}
